import useAppointmentStatus from '@hooks/useAppointmentStatus';
import { useVideoCallSignallingType } from '@hooks/useVideoCallSignallingType';
import { Room } from 'twilio-video';
import { useEffect, useState } from 'react';
import { useAppointmentRoomDT } from '@hooks/useTestPatientRoom';
import { Lobby } from '@lib/Lobby';
import { useAppointmentStats } from '@hooks/useMissedVideoCall';
import { Appointment } from 'vl-common/src/schemas/shared';

export type CallStatus = 'not-started' | 'calling' | 'in-progress' | 'completed';
export type TwilioEventStatus =
  | 'USRPOPUP'
  | 'USRWAIT'
  | 'USRCONN'
  | 'USRENDED'
  | 'CAEND'
  | 'CASTART'
  | 'END'
  | 'START'
  | 'CACALLING'
  | 'USRDECLINED'
  | 'CADECLINED'
  | 'CACHANGED';

/**
 *
 * Returns the call status of tha appointment as controlled by the clinician (using useCallStatusUpdaters) .
 * Initially read from the backend and updated in realtime via the Twilio WebSocket.
 *
 * @param appointment
 * @returns CallStatus
 */

function useCallStatusWS(appointment: Appointment): CallStatus {
  const { callStatus } = useAppointmentStatus(appointment);

  return callStatus?.status || 'not-started';
}

function useRoomDT(appointment: Appointment): Room | undefined {
  const { room } = useAppointmentRoomDT(appointment);

  return room;
}

function useCallStatusDT(appointment: Appointment): CallStatus {
  const room = useRoomDT(appointment);
  const [callStatus, setCallStatus] = useState<CallStatus>('not-started');
  const stats = useAppointmentStats(appointment);

  useEffect(() => {
    // if you have a room and lobby, take the status from there
    if (room) {
      const lobby = Lobby.forRoom(room);

      setCallStatus(lobby.callStatus);
      lobby.on('callStatusChanged', setCallStatus);
      return () => lobby.off('callStatusChanged', setCallStatus);
    }

    // otherwise, if the appointment status has loaded then take the status from there
    if (stats) {
      const { clin_start, clin_end } = stats.appointment_stati[0];

      setCallStatus((status) => {
        if (status !== 'not-started') return status;
        if (clin_end) return 'completed';
        if (clin_start) return 'in-progress';
        return 'not-started';
      });
    }

    return () => {};
  }, [room, stats]);

  return callStatus;
}

/* eslint-disable react-hooks/rules-of-hooks */
// Returns the call status of the given appointment, which *must* be stable. Switches between implementations.
export default function useCallStatus(appointment: Appointment) {
  if (useVideoCallSignallingType(appointment) === 'ws') {
    return useCallStatusWS(appointment);
  }

  return useCallStatusDT(appointment);
}
